import { ActionButton, Dropdown, type IDropdownOption } from "@fluentui/react";
import { action, makeObservable, observable } from "mobx";
import { observer } from "mobx-react";
import React from "react";
import {
    ShockEventLevel,
    SortDirection,
    type User,
    UserSortKey,
    type Vehicle,
    VehicleSortKey,
} from "../../api";
import { defaultPageSize } from "../../utils/constants";

export interface FilterShockEventListProps {
    /** This callback function allows the filter to rerender
     * the parent component. */
    setShockEventListFilter(filter: FilterShockEventListQuery): void;
}

export type FilterShockEventListQuery = {
    filteredVehicles: string[];
    filteredFleetUsers: string[];
    filteredShockLevels: ShockEventLevel[];
    isFiltered: boolean;
};

import { store } from "../../store";
import SearchableDropdown from "../atoms/searchable-dropdown";

@observer
export class FilterShockEventList extends React.Component<FilterShockEventListProps> {
    @observable private accessor filteredVehicles: string[] = [];
    @observable private accessor filteredFleetUsers: string[] = [];
    @observable private accessor filteredShockLevels: ShockEventLevel[] = [];
    @observable private accessor isFiltered: boolean = false;

    constructor(props: FilterShockEventListProps) {
        super(props);
        makeObservable(this);
    }

    private setVehicleFilter(filter: string): void {
        // Remove element if it is already clicked.
        if (this.filteredVehicles.includes(filter)) {
            this.filteredVehicles = this.filteredVehicles.filter((id) => id !== filter);
            this.setShockEventListFilter();
            return;
        }
        this.isFiltered = true;
        this.filteredVehicles = [filter, ...this.filteredVehicles];
        this.setShockEventListFilter();
    }

    private setFleetUserFilter(filter: string): void {
        // Remove element if it is already clicked.
        if (this.filteredFleetUsers.includes(filter)) {
            this.filteredFleetUsers = this.filteredFleetUsers.filter((level) => level !== filter);
            this.setShockEventListFilter();
            return;
        }
        this.isFiltered = true;
        this.filteredFleetUsers = [filter, ...this.filteredFleetUsers];
        this.setShockEventListFilter();
    }

    private setShockLevelFilter(filter: ShockEventLevel): void {
        // Remove element if it is already clicked.
        if (this.filteredShockLevels.includes(filter)) {
            this.filteredShockLevels = this.filteredShockLevels.filter((id) => id !== filter);
            this.setShockEventListFilter();
            return;
        }
        this.isFiltered = true;
        this.filteredShockLevels = [filter, ...this.filteredShockLevels];
        this.setShockEventListFilter();
    }

    @action.bound private clearFilter(): void {
        this.filteredVehicles = [];
        this.filteredFleetUsers = [];
        this.filteredShockLevels = [];
        this.isFiltered = false;
        this.setShockEventListFilter();
    }

    private setShockEventListFilter(): void {
        const filterQuery: FilterShockEventListQuery = {
            filteredVehicles: this.filteredVehicles,
            filteredFleetUsers: this.filteredFleetUsers,
            filteredShockLevels: this.filteredShockLevels,
            isFiltered: this.isFiltered,
        };
        this.props.setShockEventListFilter(filterQuery);
    }

    private formatEntry(vehicle: Vehicle): string {
        let out = "";
        if (vehicle.manufacturer) {
            out += `${vehicle.manufacturer} `;
        }
        if (vehicle.model) {
            out += `${vehicle.model} `;
        }

        if (out) {
            out += `(${vehicle.serialNumber})`;
        } else {
            out += `${vehicle.serialNumber}`;
        }

        return out;
    }

    public render(): JSX.Element {
        return (
            <>
                <SearchableDropdown
                    query={{
                        pageSize: defaultPageSize,
                        sortKey: VehicleSortKey.SERIAL_NUMBER,
                        sortDirection: SortDirection.ASCENDING,
                    }}
                    placeholder={store.i18n.t("schema.vehicle.vehicle")}
                    repository={store.repositoryVehicles}
                    formatEntity={this.formatEntry}
                    selectedKeys={this.filteredVehicles}
                    onChange={(vehicleId: string | undefined) => {
                        if (vehicleId === undefined) {
                            return;
                        }
                        this.setVehicleFilter(vehicleId);
                    }}
                    multiSelect
                />

                <SearchableDropdown
                    query={{
                        pageSize: defaultPageSize,
                        sortKey: UserSortKey.EMPLOYEE_ID,
                        sortDirection: SortDirection.ASCENDING,
                    }}
                    placeholder={store.i18n.t("schema.user.operator")}
                    repository={store.repositoryUsers}
                    formatEntity={(user: User) =>
                        `${user.firstName} ${user.lastName} (${user.employeeId})`
                    }
                    selectedKeys={this.filteredFleetUsers}
                    onChange={(userId: string | undefined) => {
                        if (userId === undefined) {
                            return;
                        }
                        this.setFleetUserFilter(userId);
                    }}
                    multiSelect
                />

                <Dropdown
                    styles={{ dropdown: { width: 200 } }}
                    options={Object.values(ShockEventLevel).map((level) => {
                        return {
                            key: level,
                            text: store.i18n.t(`schema.shockEvent.shockLevel.${level}`),
                        };
                    })}
                    placeholder={store.i18n.t("schema.shockEvent.shockLevel")}
                    selectedKeys={this.filteredShockLevels}
                    onChange={(_e, item?: IDropdownOption) => {
                        if (!item) {
                            return;
                        }
                        this.setShockLevelFilter(item.key as ShockEventLevel);
                    }}
                    multiSelect
                />

                <ActionButton
                    iconProps={{ iconName: "clearFilter" }}
                    allowDisabledFocus
                    disabled={!this.isFiltered}
                    checked={true}
                    onClick={this.clearFilter}
                ></ActionButton>
            </>
        );
    }
}
